import { DataFactory } from '../rdfjs.internal.mjs';
import { getSourceUrl } from '../resource/resource.mjs';
import { fetch } from '../fetcher.mjs';

//
const fetchDocumentLoader = async (url) => {
    const res = await fetch(url);
    return {
        contextUrl: undefined,
        documentUrl: url,
        document: await res.json(),
    };
};
/**
 * ```{note} This function is still experimental and subject to change, even
 * in a non-major release.
 * ```
 * This returns a parser that transforms a JSON-LD string into a set of RDFJS quads.
 *
 * @returns A Parser object.
 * @since 1.15.0
 */
const getJsonLdParser = () => {
    const onQuadCallbacks = [];
    const onCompleteCallbacks = [];
    const onErrorCallbacks = [];
    return {
        onQuad: (callback) => {
            onQuadCallbacks.push(callback);
        },
        onError: (callback) => {
            onErrorCallbacks.push(callback);
        },
        onComplete: (callback) => {
            onCompleteCallbacks.push(callback);
        },
        parse: async (source, resourceInfo) => {
            const jsonld = (await import('jsonld')).default;
            let quads = [];
            try {
                const plainQuads = (await jsonld.toRDF(JSON.parse(source), {
                    base: getSourceUrl(resourceInfo),
                    documentLoader: fetchDocumentLoader,
                }));
                quads = fixQuads(plainQuads);
            }
            catch (error) {
                onErrorCallbacks.forEach((callback) => callback(error));
            }
            quads.forEach((quad) => {
                onQuadCallbacks.forEach((callback) => callback(quad));
            });
            onCompleteCallbacks.forEach((callback) => callback());
        },
    };
};
/* Quads returned by jsonld parser are not spec-compliant
 * see https://github.com/digitalbazaar/jsonld.js/issues/243
 * Also, no specific type for these 'quads' is exposed by the library
 */
// change to MaybeQuad
function fixQuads(plainQuads) {
    const fixedQuads = plainQuads.map((plainQuad) => DataFactory.quad(term(plainQuad.subject), term(plainQuad.predicate), term(plainQuad.object), term(plainQuad.graph)));
    return fixedQuads;
}
function term(term) {
    switch (term.termType) {
        case "NamedNode":
            return DataFactory.namedNode(term.value);
        case "BlankNode":
            return DataFactory.blankNode(term.value.substr(2)); // Remove the '_:' prefix. see https://github.com/digitalbazaar/jsonld.js/issues/244
        case "Literal":
            return DataFactory.literal(term.value, term.language || term.datatype);
        case "DefaultGraph":
            return DataFactory.defaultGraph();
        default:
            throw Error(`unknown termType: ${term.termType}`);
    }
}

export { getJsonLdParser };
